import { Button, Icon, Sheet, useToast } from '../ui';
import { useEffect, useState, useSyncExternalStore } from '../app-owner/react';
import { services } from '../adapters';
import { BUILD_BACKEND } from '../config/backend';
import { formatDate, formatTime, t } from '../i18n';
import { enterDemoOwner, jumpClock, nextReminderAt } from './session';
import { navigate, useRoute } from '../app-owner/router';
import { useSheetFlag } from './useSheetFlag';

/** Stable reference so `data-now` moves only when the clock is advanced (QA compares two reads exactly). */
const LOAD_AT = Math.floor(Date.now() / 60_000) * 60_000;

function useClock(): { now: number; offset: number } {
  const snap = useSyncExternalStore(
    (cb: () => void) => services.clock.subscribe(cb),
    () => services.clock.offsetMs(),
    () => 0,
  );
  const [tick, setTick] = useState(0);
  useEffect(() => { const id = setInterval(() => setTick((x: number) => x + 1), 15_000); return () => clearInterval(id); }, []);
  void tick;
  return { now: services.clock.now(), offset: snap };
}

export function whenLabel(ms: number): string {
  return `${formatDate(ms, 'long')} · ${formatTime(ms)}`;
}

/** Runs a clock action and reports its effects in one toast (clock jump, reminders sent, deposits expired). */
export function useClockActions() {
  const toast = useToast();
  const run = async (action: number | { to: number }) => {
    const r = await jumpClock(action);
    const parts = [t('common.toast.clockJump', { when: whenLabel(Date.parse(r.when)) })];
    if (r.sent > 0) parts.push(t('owner.demo.clock.sent', { messages: t('common.unit.messages', { count: r.sent }) }));
    if (r.expired > 0) parts.push(t('owner.demo.clock.expired', { count: r.expired }));
    toast.show({ message: parts.join(' · '), duration: 4000 });
    document.querySelector('[data-testid=demo-banner-clock]')?.classList.add('ow-pulse');
    setTimeout(() => document.querySelector('[data-testid=demo-banner-clock]')?.classList.remove('ow-pulse'), 700);
  };
  return {
    hour: () => run(60 * 60_000),
    day: () => run(24 * 60 * 60_000),
    week: () => run(7 * 24 * 60 * 60_000),
    reminder: async () => run({ to: await nextReminderAt() }),
    real: () => run({ to: Date.now() }),
  };
}

export function ClockControls({ compact }: { compact?: boolean }) {
  const { now, offset } = useClock();
  const act = useClockActions();
  return (
    <div className="ow-clock" data-testid="demo-advance-clock" data-now={new Date(LOAD_AT + offset).toISOString()}>
      <p className="ow-clock__now"><span data-testid="mock-label" data-mock="clock" className="ow-mock"><Icon name="clock" size={16} />{t('owner.demo.clock.now', { when: '' })}<bdi dir="ltr" className="pl-num">{formatDate(now, 'dayMonth')} {formatTime(now)}</bdi></span></p>
      <div className={compact ? 'pl-grid-2' : 'pl-grid-2 ow-clock__grid'}>
        <Button variant="secondary" onClick={act.hour} data-testid="demo-advance-1h">{t('owner.demo.clock.hour')}</Button>
        <Button variant="secondary" onClick={act.day} data-testid="demo-advance-24h">{t('owner.demo.clock.day')}</Button>
        <Button variant="secondary" onClick={act.week} data-testid="demo-advance-7d">{t('owner.x.clock.week')}</Button>
        <Button variant="secondary" onClick={act.reminder} data-testid="demo-advance-reminder">{t('owner.demo.clock.reminder')}</Button>
      </div>
      <Button variant="ghost" block onClick={act.real} data-testid="demo-clock-real">{t('owner.demo.clock.real')}</Button>
      <p className="pl-muted pl-small">{t('owner.demo.clock.warn')}</p>
    </div>
  );
}

export function DemoBanner() {
  if (BUILD_BACKEND === 'supabase') return null;   // the demo banner belongs to the browser-only demo, never to a real backend
  const { now, offset } = useClock();
  const route = useRoute();
  const toast = useToast();
  const [open, setOpen] = useSheetFlag();
  const [busy, setBusy] = useState(false);
  const reset = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await services.demo.reset();
      if (route.path.startsWith('/app') && !route.path.startsWith('/app/start')) { await enterDemoOwner(); }
      toast.show({ message: t('common.toast.demoReset'), duration: 2500 });
    } finally { setBusy(false); }
  };
  return (
    <>
      <div className="ow-demo" data-testid="demo-banner" role="status">
        <span className="ow-demo__text"><Icon name="info" size={16} /><span>{t('common.demo.banner.text')}</span></span>
        <button type="button" className="ow-demo__btn ow-demo__clock pl-press" data-testid="demo-banner-clock" onClick={() => setOpen(true)} aria-label={t('owner.demo.clock.title')}>
          <span className={offset !== 0 ? 'ow-demo__pill is-shifted' : 'ow-demo__pill'}><Icon name="clock" size={14} /><bdi dir="ltr" className="pl-num">{formatTime(now)}</bdi></span>
        </button>
        <button type="button" className="ow-demo__btn pl-press" data-testid="demo-banner-reset" onClick={reset}><span className="ow-demo__pill">{t('common.demo.banner.reset')}</span></button>
      </div>
      <Sheet open={open} onClose={() => setOpen(false)} title={t('owner.demo.clock.title')}>
        <div data-testid="sheet"><ClockControls compact /></div>
      </Sheet>
    </>
  );
}
void navigate;
